import {useState} from 'react'
import {useSelector} from 'react-redux'
import {authorizationHeaderFromAccessToken} from '../auth'
import {API_URL} from '../config'
import {captureException} from '../errors'
import ServerError from '../errors/server'
import {getAccessToken} from '../store/authentication/authentication.selectors'
import {Sponsor, SponsorType} from './useSponsors'

type Options = Readonly<{
  onError?: (error: Error) => void
  onSuccess?: (sponsor: Sponsor) => void
}>

function useCreateSponsor({onError, onSuccess}: Options = {}): {
  busy: boolean
  createSponsor: (name: string, type: SponsorType, image?: File) => void
  error: Error | null
} {
  const accessToken = useSelector(getAccessToken)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const createSponsor = (name: string, type: SponsorType, image?: File) => {
    setBusy(true)
    setError(null)

    fireCreateSponsor(accessToken, name, type, image)
      .then(sponsor => onSuccess?.(sponsor))
      .catch(error => {
        captureException(error)
        setError(error)
        onError?.(error)
      })
      .finally(() => setBusy(false))
  }

  return {busy, createSponsor, error}
}

async function fireCreateSponsor(
  accessToken: string,
  name: string,
  type: SponsorType,
  image?: File
): Promise<Sponsor> {
  const url = new URL('sponsors', API_URL)

  const formData = new FormData()
  formData.append('name', name)
  formData.append('type', type)
  if (image) {
    formData.append('image', image)
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      ...authorizationHeaderFromAccessToken(accessToken)
    },
    body: formData
  })

  if (response.status !== 201) {
    return ServerError.fromResponse(response)
  }

  return response.json()
}

export default useCreateSponsor
